import React, { Fragment } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import SelectionIndicator from 'components/selection-indicator';
import { Size } from 'components/selection-indicator/constants';
import { useLocale } from 'state/intl';
import { formatCurrencyCodeForRegion } from 'utils/payment/format-currency-code-for-region';

import { Disclaimer, LanguageSelectionContainer, ModalAdditionalContentWrapper } from './styled';

const LanguageSelectionCell = styled.div`
  height: 100%;
  border-bottom: 1px solid #eeeaea;
`;

const LanguageSelectionLanguageText = styled.span`
  font-weight: ${Styles.fontWeight.heavy};
  padding-bottom: 2px;
`;

const LanguageSelectionLabel = styled(LanguageSelectionCell)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 12px 24px;
`;

const LanguageIndicatorCell = styled(LanguageSelectionCell)`
  position: relative;
`;

interface ILanguageModalContentsProps {
  regionFilter: (region: string) => boolean;
  disclaimer?: string;
  applyButtonText: string;
  setLanguage: () => void;
  handleSelection: (
    selectedLanguage: string,
    mappedRegion: string,
    isOneStep: boolean
  ) => () => void;
  regionKey: string;
  langKey: string;
}

const LanguageSelectorModalContents: React.FC<ILanguageModalContentsProps> = ({
  regionFilter,
  regionKey,
  langKey,
  disclaimer,
  applyButtonText,
  setLanguage,
  handleSelection
}) => {
  const { formatMessage } = useIntl();
  const { readableRegions, readableLanguages, supportedLocales } = useLocale();

  const regionLanguageSelections = supportedLocales
    .filter(({ region: filteredRegion }) => regionFilter(filteredRegion))
    .map(({ region: mappedRegion, language: mappedLanguage, currencyCodeFormat }) => (
      <Fragment key={`dialog-button-${mappedLanguage}-${mappedRegion}`}>
        <LanguageSelectionCell />
        <LanguageIndicatorCell
          data-testid={`dialog-button-${mappedLanguage.toLowerCase()}-${mappedRegion.toLowerCase()}`}
          onClick={handleSelection(mappedLanguage, mappedRegion, false)}
        >
          <SelectionIndicator
            option={{
              name: {
                _type: 'localeString',
                locale: `${mappedLanguage.toLowerCase()}-${mappedRegion.toLowerCase()}`
              }
            }}
            checked={regionKey === mappedRegion && langKey === mappedLanguage}
            type="radio"
            aria-label={`${formatMessage(messages.select)} ${
              readableLanguages[mappedLanguage]
            } / ${mappedRegion}`}
            size={Size.REGULAR}
            uiPattern="radio"
            hasBackground
          />
        </LanguageIndicatorCell>
        <LanguageSelectionLabel>
          <LanguageSelectionLanguageText lang={mappedLanguage.toLowerCase()}>
            {readableLanguages[mappedLanguage]}
          </LanguageSelectionLanguageText>
          <span
            data-testid={`dialog-code-${mappedLanguage.toLowerCase()}-${mappedRegion.toLowerCase()}`}
          >
            {formatCurrencyCodeForRegion(readableRegions[mappedRegion], currencyCodeFormat)}
          </span>
        </LanguageSelectionLabel>
        <LanguageSelectionCell />
      </Fragment>
    ));

  return (
    <>
      <LanguageSelectionContainer>{regionLanguageSelections}</LanguageSelectionContainer>
      {disclaimer && (
        <ModalAdditionalContentWrapper>
          <Disclaimer>{disclaimer}</Disclaimer>
        </ModalAdditionalContentWrapper>
      )}
      <ActionButton onClick={setLanguage} data-testid="action-button-apply-language-selection">
        {applyButtonText}
      </ActionButton>
    </>
  );
};

export default LanguageSelectorModalContents;

const messages = defineMessages({
  select: {
    id: 'select',
    defaultMessage: 'Select',
    description: 'Part of aria label letting the user to select a language and region option'
  }
});
